/**
 * DenseSubmitFooter — F-003.4 Phase 4 (IMPL-P4-006).
 *
 * Footer sticky per il dense form (AGENTE/COMMERCIALISTA): single-line
 * compatto con PDFDropzone (compact) + ResponsibilityCheckbox (compact) +
 * bottone "Invia pratica".
 *
 * Pattern (vedi ContractDenseForm sticky footer):
 *  - `sticky bottom-0` per restare visibile durante lo scroll del form.
 *  - `aria-disabled` (no native `disabled`) → annunciabile da screen reader.
 *  - Errori in `<p role="alert" aria-live="assertive">` per annuncio
 *    immediato all'utilizzatore di tastiera/screen reader.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { sha256Hex } from '@/lib/sha256';
import { PDFDropzone } from '../PDFDropzone';
import { ResponsibilityCheckbox } from '../ResponsibilityCheckbox';
import {
  submit,
  isPaymentRequired,
  type PdfConfirmResponse,
  type PraticaView,
} from '../../lib/pratica-client';
import { resolveSubmitError, type SubmitErrorView } from '../../lib/submit-error';
import { CONSENT_TEXT } from '../../lib/consent';

export interface DenseSubmitFooterProps {
  praticaId: string;
  onSubmitted: (pratica: PraticaView) => void;
}

export function DenseSubmitFooter({ praticaId, onSubmitted }: DenseSubmitFooterProps) {
  const [pdfInfo, setPdfInfo] = useState<PdfConfirmResponse | null>(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentTextHash, setConsentTextHash] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<SubmitErrorView | null>(null);

  useEffect(() => {
    let cancelled = false;
    sha256Hex(CONSENT_TEXT).then((hash) => {
      if (!cancelled) setConsentTextHash(hash);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const canSubmit = !!pdfInfo && consentGiven && !!consentTextHash && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await submit(praticaId, {
        consentGiven: true,
        consentTextHash,
      });
      // Gli agenti pagano col wallet (no checkout); difensivo: se mai arrivasse
      // un paymentRequired, reindirizza al Checkout.
      if (isPaymentRequired(result)) {
        window.location.href = result.checkoutUrl;
        return;
      }
      onSubmitted(result);
    } catch (err) {
      setError(resolveSubmitError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      id="dense-submit-footer"
      className="sticky bottom-0 bg-background border-t pt-3 pb-4 mt-4 space-y-3 z-10"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <PDFDropzone
            praticaId={praticaId}
            onUploaded={setPdfInfo}
            onError={(message) => setError({ message })}
            variant="compact"
          />
        </div>
        <ResponsibilityCheckbox
          variant="compact"
          checked={consentGiven}
          onCheckedChange={setConsentGiven}
          id="dense-submit-consent"
        />
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          aria-disabled={!canSubmit}
          aria-describedby={!canSubmit ? 'dense-submit-footer-help' : undefined}
          className={cn(!canSubmit && 'opacity-50 cursor-not-allowed')}
        >
          {submitting ? 'Invio...' : 'Invia pratica'}
        </Button>
      </div>

      {error && (
        <p role="alert" aria-live="assertive" className="text-sm text-destructive">
          {error.message}
          {error.action && (
            <>
              {' '}
              <a
                href={error.action.href}
                className="font-medium underline underline-offset-2"
              >
                {error.action.label}
              </a>
            </>
          )}
        </p>
      )}

      <div id="dense-submit-footer-help" className="sr-only" aria-live="polite">
        {!pdfInfo && 'Carica il PDF. '}
        {!consentGiven && 'Spunta la casella di responsabilità.'}
      </div>
    </div>
  );
}
